/**
 * UPDATE EXAMPLES BUTTON v1.1
 * ✅ FIX CRÍTICO: Al guardar recetas, mapea componentId estáticos → IDs reales en Firestore.
 * ✅ LIMPIEZA PROFUNDA: Borra el catálogo de ejemplo viejo antes de inyectar el nuevo.
 * ✅ FIX MATEMÁTICO: Ahora el cálculo de stock para las porciones es 100% exacto.
 * ✅ NUEVAS RECETAS: Se agregaron Panchos, Yogur con Granola y se corrigieron las Tartas.
 */
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useFirebase } from '../context/FirebaseContext';
import { useApp } from '../context/AppContext';
import { SAMPLE_COMPONENTS } from '../models/simpleComponents';
import { SAMPLE_RECIPES } from '../models/simpleRecipes';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { doc, setDoc, getDoc, getDocs, collection, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const CURRENT_VERSION = '1.2'; // <-- ¡Reinicio de versión a 1.1 con la base de datos limpia!

// Normaliza nombre para comparación fuzzy
const normalizeName = (name) =>
  (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

export default function UpdateExamplesButton() {
  const { isAuthenticated } = useAuth();
  const { household } = useFirebase();
  const { components, recipes } = useApp();

  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showBanner, setShowBanner] = useState(
    !sessionStorage.getItem('mealcycle-update-banner-dismissed')
  );

  useEffect(() => {
    checkVersion();
  }, [isAuthenticated, household]);

  const checkVersion = async () => {
    if (!isAuthenticated || !household?.id) return;
    try {
      const versionRef = doc(db, 'households', household.id, 'metadata', 'examplesVersion');
      const versionSnap = await getDoc(versionRef);
      const storedVersion = versionSnap.exists() ? versionSnap.data().version : null;
      if (storedVersion !== CURRENT_VERSION) {
        setNeedsUpdate(true);
      }
    } catch (error) {
      console.error('Error verificando versión:', error);
    }
  };

  const handleUpdate = async () => {
    if (!household?.id) {
      toast.error('No hay hogar configurado');
      return;
    }

    setUpdating(true);
    const loadingToast = toast.loading('Forzando actualización del catálogo...');

    try {
      const compCollection = collection(db, `households/${household.id}/components`);
      const recCollection = collection(db, `households/${household.id}/recipes`);

      // ==========================================
      // 0. LIMPIEZA PROFUNDA (ELIMINAR VIEJOS)
      // ==========================================
      toast.loading('Limpiando catálogo viejo...', { id: loadingToast });
      
      // Borrar componentes de ejemplo viejos
      const oldComps = await getDocs(compCollection);
      for (const d of oldComps.docs) {
        if (d.data().isSample === true) {
          await deleteDoc(doc(compCollection, d.id));
        }
      }

      // Borrar recetas de ejemplo viejas
      const oldRecs = await getDocs(recCollection);
      for (const d of oldRecs.docs) {
        if (d.data().isSample === true) {
          await deleteDoc(doc(recCollection, d.id));
        }
      }

      // ==========================================
      // 1. CARGAR COMPONENTES NUEVOS
      // ==========================================
      toast.loading('Inyectando nuevos ingredientes...', { id: loadingToast });

      for (const comp of SAMPLE_COMPONENTS) {
        await setDoc(doc(compCollection, comp.id), {
          ...comp,
          isSample: true,
          updatedAt: new Date(),
        });
      }

      // ==========================================
      // 2. LEER TODOS LOS COMPONENTES DE FIRESTORE
      // ==========================================
      toast.loading('Enlazando recetas...', { id: loadingToast });
      const compSnap = await getDocs(compCollection);
      const allComponents = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const nameToId = {};
      allComponents
        .filter(c => c.id.includes('_') && c.id.split('_').length > 2)
        .forEach(c => { nameToId[normalizeName(c.name)] = c.id; });
      allComponents
        .filter(c => !c.id.includes('_') || c.id.split('_').length <= 2)
        .forEach(c => { nameToId[normalizeName(c.name)] = c.id; });

      SAMPLE_COMPONENTS.forEach(c => {
        nameToId[normalizeName(c.name)] = c.id;
      });

      console.log('[UpdateExamples] Mapa nombre→id construido:', Object.keys(nameToId).length, 'entradas');

      // ==========================================
      // 3. CARGAR RECETAS NUEVAS
      // ==========================================
      let recsAdded = 0;
      let recsMapped = 0;
      let recsMissingComp = 0;

      for (const rec of SAMPLE_RECIPES) {
        const mappedMain = mapComponent(rec.main, nameToId);
        if (!mappedMain) {
          console.warn('[UpdateExamples] Componente principal no encontrado para:', rec.name, rec.main);
          recsMissingComp++;
          continue;
        }

        const mappedSides = (rec.sides || []).reduce((acc, side) => {
          if (!side) return acc;
          const mapped = mapComponent(side, nameToId);
          if (mapped) {
            acc.push(mapped);
          } else {
            console.warn(`[UpdateExamples] Rescatando guarnición no mapeada: ${side.componentName}`);
            acc.push(side);
          }
          return acc;
        }, []);

        const hadMappingChanges =
          mappedMain.componentId !== rec.main.componentId ||
          mappedSides.some((s, i) => s.componentId !== (rec.sides[i]?.componentId));

        if (hadMappingChanges) recsMapped++;

        await setDoc(doc(recCollection, rec.id), {
          ...rec,
          main: mappedMain,
          sides: mappedSides,
          isSample: true,
          updatedAt: new Date(),
        });
        recsAdded++;
      }

      console.log(`[UpdateExamples] Recetas: ${recsAdded} guardadas, ${recsMapped} con IDs mapeados, ${recsMissingComp} omitidas`);

      // ==========================================
      // 4. GUARDAR VERSIÓN
      // ==========================================
      toast.loading('¡Guardando cambios!', { id: loadingToast });
      const versionRef = doc(db, 'households', household.id, 'metadata', 'examplesVersion');
      await setDoc(versionRef, {
        version: CURRENT_VERSION,
        updatedAt: new Date(),
        componentsAdded: SAMPLE_COMPONENTS.length,
        recipesAdded: recsAdded,
        recipesMapped: recsMapped,
      });

      toast.success(
        `🎉 ¡Catálogo actualizado a v1.1! Se ajustaron las porciones y el stock.`,
        { id: loadingToast, duration: 4000 }
      );

      setNeedsUpdate(false);
      setShowBanner(false);
      setTimeout(() => window.location.reload(), 2000);

    } catch (error) {
      console.error('Error forzando actualización:', error);
      toast.error('Error al actualizar. Revisá tu conexión.', { id: loadingToast });
    } finally {
      setUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('mealcycle-update-banner-dismissed', 'true');
  };

  if (!needsUpdate || !showBanner || !isAuthenticated || !household?.id) {
    return null;
  }

  return (
    <div className="fixed bottom-20 sm:bottom-4 right-4 left-4 sm:left-auto z-50 animate-slideUp">
      <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-4 rounded-xl shadow-2xl max-w-sm mx-auto sm:mx-0 border border-blue-400/30">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <SparklesIcon className="w-6 h-6 text-yellow-300" />
          </div>

          <div className="flex-1">
            <h3 className="font-bold mb-1 flex items-center gap-2">
              ¡Actualización Crítica!
              <span className="bg-yellow-400 text-blue-900 px-2 py-0.5 rounded-full text-xs font-black">
                v{CURRENT_VERSION}
              </span>
            </h3>

            <p className="text-sm text-blue-100 mb-3 leading-tight">
              Limpiamos el catálogo, <strong>corregimos el cálculo matemático de porciones</strong> y agregamos nuevas recetas.{' '}
              <span className="text-white font-medium mt-2 inline-block">
                🛡️ Tus recetas personales están a salvo.
              </span>
            </p>

            <button
              onClick={handleUpdate}
              disabled={updating}
              className="w-full bg-white text-blue-700 px-4 py-2.5 rounded-lg font-bold hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg active:scale-95"
            >
              {updating ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  Instalando v1.1...
                </span>
              ) : (
                'Obtener Mejora'
              )}
            </button>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 w-6 h-6 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Dado un objeto {componentId, componentName, servings} y el mapa nombre→id,
 * devuelve el objeto con el componentId correcto (el que existe en Firestore).
 * Estrategia: primero intenta por nombre exacto normalizado, luego por substring.
 * Devuelve null si no puede mapear.
 */
function mapComponent(compRef, nameToId) {
  if (!compRef) return null;

  // 1. Si el ID estático ya existe en el mapa directamente, usarlo
  //    (significa que el componente se cargó con setDoc y su ID estático es válido)
  const staticEntry = Object.entries(nameToId).find(([, id]) => id === compRef.componentId);
  if (staticEntry) {
    return { ...compRef }; // El ID ya es válido
  }

  // 2. Buscar por nombre normalizado exacto
  const normName = normalizeName(compRef.componentName);
  if (nameToId[normName]) {
    return { ...compRef, componentId: nameToId[normName] };
  }

  // 3. Buscar por substring (el nombre del compRef está contenido en alguna clave)
  const partialMatch = Object.keys(nameToId).find(
    key => key.includes(normName) || normName.includes(key)
  );
  if (partialMatch) {
    return { ...compRef, componentId: nameToId[partialMatch] };
  }

  // 4. No se encontró — devolver el original (puede fallar al matchear stock, pero al menos no rompe)
  console.warn('[mapComponent] No se encontró ID para:', compRef.componentName);
  return { ...compRef };
}
